/* @ts-ignore */
import queryStringHandler from "query-string-handler";
import { scrollToElement } from "@/components/global/scroll-to-element";

interface FacetCollectionItem {
  title: string;
  facets: FacetItem[];
}
interface FacetItem {
  isDisabled: boolean;
  isSelected: boolean;
  label: string;
}

interface ResultItem {
  categories?: string[];
  category?: string;
  contentType?: string;
  contentTypes?: string[];
  description?: string;
  image?: ResultItemImage;
  isCardResource?: boolean;
  title?: string;
  url?: string;
  card?: ResultCardOptions;
}
interface ResultItemImage {
  url?: string;
  altText?: string;
}
interface ResultCardOptions {
  documentUrl?: string;
  link?: ResultCardLink;
  videoId?: string;
}
interface ResultCardLink {
  href?: string;
  target?: string;
  text?: string;
  title?: string;
}

const initResourceModals = () => {
  const resourceIndex = document.querySelector<HTMLDivElement>(".js-resource-index");

  if (resourceIndex) {
    const resourceModals = resourceIndex.querySelectorAll(".js-global-modal-block");

    if (resourceModals && resourceModals.length > 0) {
      resourceModals.forEach((item) => {
        const openBtn = item.querySelectorAll<HTMLButtonElement>(".js-open-modal-btn");
        const closeBtn = item.querySelector<HTMLButtonElement>(".js-close-modal-btn");
        const trapFocusBtn = item.querySelector<HTMLButtonElement>(".js-trap-focus-btn");
        const wrapper = item.querySelector<HTMLDivElement>(".js-modal-wrapper");
        const modal = item.querySelector<HTMLDivElement>(".js-modal");

        if (openBtn && closeBtn && trapFocusBtn && wrapper && modal) {
          const handleClickOutside = (e: MouseEvent) => {
            const target = e.target as HTMLElement;

            if (target.classList.contains("js-modal-wrapper")) {
              closeModal();
            }
          };

          const handleEscape = (e: KeyboardEvent) => {
            if (e.key === "Escape") {
              closeModal();
            }
          };

          const handleTabIndex = (isFocus: boolean) => {
            const focusableSelectors = `
                  a[href],
                  area[href],
                  input:not([disabled]),
                  select:not([disabled]),
                  textarea:not([disabled]),
                  button:not([disabled]),
                  iframe,
                  object,
                  embed
              `;

            const focusItems = modal.querySelectorAll<HTMLElement>(focusableSelectors);

            focusItems.forEach((item) => {
              if (isFocus) {
                item.tabIndex = 0;
              } else {
                item.tabIndex = -1;
              }
            });
          };

          const handleResetIframe = () => {
            const iframe = item.querySelector<HTMLIFrameElement>("iframe");
            // resets and stops iframe from playing on close
            if (iframe) {
              iframe.src = "";
            }
          };

          const handleIframeSrc = (id: string, title: string) => {
            const iframe = item.querySelector<HTMLIFrameElement>("iframe");
            // resets and stops iframe from playing on close
            if (iframe) {
              iframe.src = `//play.vidyard.com/${id}.html?`;
              if (title) {
                iframe.title = title;
              }
            }
          };

          const openModal = () => {
            wrapper.setAttribute("aria-hidden", "false");
            wrapper.setAttribute("data-isOpen", "true");
            handleTabIndex(true);
            document.addEventListener("keydown", handleEscape);
          };

          const closeModal = () => {
            wrapper.setAttribute("aria-hidden", "true");
            wrapper.setAttribute("data-isOpen", "false");
            handleTabIndex(false);
            handleResetIframe();
            document.removeEventListener("keydown", handleEscape);
          };

          const handleResetFocus = () => {
            // taps focus and resets to first item
            closeBtn.focus();
          };

          // handle cms content and update tab index on load to -1
          handleTabIndex(false);

          document.addEventListener("click", handleClickOutside);

          openBtn.forEach((btn) => {
            btn.addEventListener("click", () => {
              const id = btn.getAttribute("data-videoId");
              const title = btn.getAttribute("data-videoTitle") || "";

              if (id) {
                handleIframeSrc(id, title);
              }
              openModal();
            });
          });
          closeBtn.addEventListener("click", () => {
            closeModal();
          });
          trapFocusBtn.addEventListener("focus", handleResetFocus);
        }
      });
    }
  }
};

export default function ResourceIndexPage() {
  const resourceIndex = document.querySelector<HTMLDivElement>(".js-resource-index");
  const filtersMenu = document.querySelector<HTMLDivElement>(".js-filters-mobile-menu");
  const fixedMenuWrapper = document.querySelector<HTMLDivElement>(".js-fixed-menu");

  if (resourceIndex && filtersMenu && fixedMenuWrapper) {
    const selectFilters = resourceIndex.querySelectorAll<HTMLSelectElement>(".js-filter-item");
    const selectFilterBtns = resourceIndex.querySelectorAll<HTMLButtonElement>(".js-filter-btn");
    const resultsWrapper = resourceIndex.querySelector<HTMLDivElement>(".js-results-wrapper");
    const resultsList = resourceIndex.querySelector<HTMLDivElement>(".js-results-list");
    const prevPageBtn = resourceIndex.querySelector<HTMLButtonElement>(".js-prev-btn");
    const nextPageBtn = resourceIndex.querySelector<HTMLButtonElement>(".js-next-btn");
    const clearBtns = resourceIndex.querySelectorAll<HTMLButtonElement>(".js-clear-btn");
    const openBtn = filtersMenu.querySelector<HTMLButtonElement>(".js-open-menu-btn");
    const cancelBtn = filtersMenu.querySelector<HTMLButtonElement>(".js-cancel-btn");
    const applyBtn = filtersMenu.querySelector<HTMLButtonElement>(".js-apply-btn");
    const mobileFilterItems = filtersMenu.querySelectorAll<HTMLDivElement>(".js-mobile-filter-item");
    const mobileFilterBtns = filtersMenu.querySelectorAll<HTMLButtonElement>(".js-mobile-filter-btn");
    let totalPages: number = 0;
    let savedSrt: number = -1;
    let savedCat = "";
    let savedType = "";
    let savedPage = 0;
    let page: number = 1;
    let srt: number = 1;
    let type: string = "";
    let cat: string = "";
    let results: ResultItem[] = [];
    let disabledFacets: string[] = [];

    const hanldeCreateResourceCard = (title?: string, description?: string, contentType?: string, image?: ResultItemImage) => {
      return `
        <div class="rounded-3xl border bg-white border-neutral-50 w-full h-full p-5">
          <div class="flex flex-col h-full gap-5">
              ${
                image
                  ? `
                  <div class="relative w-full aspect-video rounded-xl overflow-hidden">
                      <img src="${image.url}" alt="${image.altText}" class="absolute inset-0 w-full h-full object-cover" width="289" loading="lazy" />
                  </div>
                `
                  : ""
              }
                  
              <div class="flex-1 flex flex-col gap-5 items-start text-left">
                ${
                  contentType
                    ? `
                      <div class="text-primary-600 eyebrow">${contentType}</div>
                  `
                    : ""
                }
                ${
                  title
                    ? `
                      <h3 class="h4-mobile lg:h4 text-primary-600">${title}</h3>
                  `
                    : ""
                }
                ${
                  description
                    ? `
                      <div class="text-neutral-500 body-sm line-clamp-3">${description}</div>
                  `
                    : ""
                }
              </div>
          </div>
      </div>
      `;
    };

    const handleCreateVideoCard = (item: ResultItem) => {
      return `
        <article class="js-result-item col-span-1">
            <div class="js-global-modal-block h-full"> 
                <button data-videoId="${item.card?.videoId}" data-videoTitle="${item.title}" class="js-open-modal-btn h-full rounded-2xl w-full hover:no-underline">
                    ${hanldeCreateResourceCard(item.title, item.description, item.contentType, item.image)}
                </button>

                <div data-isOpen="false" 
                    class="
                        js-modal-wrapper
                        group/modal
                        fixed inset-0 bg-primary-800/60 z-[9999]
                        flex items-center justify-center
                        data-[isOpen=true]:opacity-100 
                        data-[isOpen=false]:opacity-0 data-[isOpen=false]:pointer-events-none
                        global-transition" 
                    role="dialog" aria-hidden="true" aria-modal="true" aria-labelledby="Video Player">

                    <div class="js-modal container mx-auto" tabindex="-1">
                        <div class="relative">
                            <button tabindex="-1" class="js-close-modal-btn focus:shadow-focus absolute top-4 right-4 z-10 h-10 w-10 rounded-full border border-primary-500 bg-primary-600 p-2 hover:no-underline flex items-center justify-center">
                                <span class="h-10 w-10 flex items-center justify-center">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 13 13" fill="none">
                                        <rect y="2.60938" width="3.69018" height="14.6938" transform="rotate(-45 0 2.60938)" fill="#FFF"/>
                                        <rect x="10.3906" width="3.69018" height="14.6938" transform="rotate(45 10.3906 0)" fill="#FFF"/>
                                    </svg>
                                </span>
                                <span class="sr-only">Close Modal</span>
                            </button>
                            <div class="max-h-[calc(100vh-100px)] overflow-auto relative flex justify-center group-data-[isOpen=true]/modal:scale-100 group-data-[isOpen=false]/modal:scale-90 global-transition">                                                  
                                <div class="flex w-full relative aspect-video">
                                    <iframe class="vidyard_iframe h-full w-full" width="640" height="360" scrolling="no" frameborder="0" allowtransparency="true" allowfullscreen referrerpolicy="no-referrer-when-downgrade"></iframe>
                                </div>
                            </div>
                            <button tabindex="-1" class="js-trap-focus-btn sr-only">
                                Refocus first element
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </article>
      `;
    };

    const handleCreateDetailPageCard = (item: ResultItem) => {
      return `
        <article class="js-result-item col-span-1">
          <a href="${item.url}" class="rounded-3xl w-full hover:no-underline">
              ${hanldeCreateResourceCard(item.title, item.description, item.contentType, item.image)}
          </a>
        </article>
      `;
    };

    const handleCreateLinkCard = (item: ResultItem) => {
      return `
        <article class="js-result-item col-span-1">
          <a href="${item.card?.link?.href}" target="${item.card?.link?.target}" aria-label="${item.card?.link?.title}" class="rounded-3xl w-full hover:no-underline">
            ${hanldeCreateResourceCard(item.title, item.description, item.contentType, item.image)}
          </a>
        </article>
      `;
    };

    const handleCreateDocumentCard = (item: ResultItem) => {
      return `
        <article class="js-result-item col-span-1">
          <a href="${item.card?.documentUrl}" target="_blank" download class="rounded-3xl w-full hover:no-underline">
              ${hanldeCreateResourceCard(item.title, item.description, item.contentType, item.image)}
          </a>
        </article>
      `;
    };

    const handleNewCards = () => {
      if (results.length > 0) {
        resourceIndex.setAttribute("data-noresults", "false");
        resultsWrapper?.setAttribute("data-results", "true");

        results.forEach((item: ResultItem) => {
          if (item.isCardResource) {
            if (item.card?.documentUrl) {
              resultsList?.insertAdjacentHTML("beforeend", handleCreateDocumentCard(item));
            } else if (item.card?.link?.href) {
              resultsList?.insertAdjacentHTML("beforeend", handleCreateLinkCard(item));
            } else if (item.card?.videoId) {
              resultsList?.insertAdjacentHTML("beforeend", handleCreateVideoCard(item));
            }
          } else {
            resultsList?.insertAdjacentHTML("beforeend", handleCreateDetailPageCard(item));
          }
        });
      } else {
        resourceIndex.setAttribute("data-noresults", "true");
      }
    };

    const handleRemoveCards = () => {
      resourceIndex.setAttribute("data-results", "false");
      const resultItems = resultsList?.querySelectorAll<HTMLDivElement>(".js-result-item");

      if (resultItems && resultItems.length > 0) {
        resultItems.forEach((item) => item.remove());
      }
    };

    const handleDisabledFacetBtns = () => {
      if (selectFilterBtns && selectFilterBtns.length > 0) {
        selectFilterBtns.forEach((btn) => {
          const text = btn.innerText;
          if (disabledFacets.includes(text.trim())) {
            btn.disabled = true;
          } else {
            btn.disabled = false;
          }
        });
      }
      if (mobileFilterBtns && mobileFilterBtns.length > 0) {
        mobileFilterBtns.forEach((btn) => {
          const text = btn.innerText;
          if (disabledFacets.includes(text.trim())) {
            btn.disabled = true;
          } else {
            btn.disabled = false;
          }
        });
      }
    };

    const handleFetchResults = async (pg: number, cat: string, type: string, sort: number) => {
      try {
        const url = `/api/search/resource-detail?pg=${pg}&cat=${cat}&typ=${type}&srt=${sort}`;

        const response = await fetch(url);

        if (!response.ok) {
          throw new Error(`Request failed with status ${response.status}`);
        }

        const data = await response.json();
        // clear results array
        results = [];
        disabledFacets = [];
        handleRemoveCards();

        if (data.items && data.items.length > 0) {
          results = data.items;
          resourceIndex.setAttribute("data-noresults", "false");
        } else {
          resourceIndex.setAttribute("data-noresults", "true");
        }

        if (data.facetsCollections && data.facetsCollections.length > 0) {
          data.facetsCollections.forEach((collection: FacetCollectionItem) => {
            if (collection.facets && collection.facets.length > 0) {
              const disabledItems: string[] = collection.facets.filter((facet) => facet.isDisabled).map((item) => item.label);

              if (disabledItems) {
                disabledFacets = [...disabledItems];
              }
            }
          });

          handleDisabledFacetBtns();
          handleUpdateSelectValue("cat", cat);
          handleUpdateSelectValue("srt", sort.toString());
          handleUpdateSelectValue("typ", type);
          handleUpdateClearBtn();
        }

        totalPages = data.totalPages;

        handleNewCards();
        initResourceModals(); // reinit modal event listeners
        handleDisabledPagination();
        resourceIndex.setAttribute("data-loading", "false");
      } catch (error) {
        console.error("Error during GET request:", error);
        resourceIndex.setAttribute("data-noresults", "true");
        resourceIndex.setAttribute("data-loading", "false");
      }
    };

    const handleClearFilters = () => {
      cat = "";
      type = "";
      srt = 1;
      page = 1;
      queryStringHandler.replace({ cat: "" });
      queryStringHandler.replace({ typ: "" });
      queryStringHandler.replace({ srt: "1" });
      queryStringHandler.replace({ pg: "1" });

      handleFetchResults(page, cat, type, srt);
    };

    const handleUpdateQuery = (query: string, value: string) => {
      queryStringHandler.replace({ [query]: value });

      if (query === "cat") {
        cat = value;
      }
      if (query === "typ") {
        type = value;
      }
      if (query === "srt") {
        srt = parseInt(value);
      }
      // reset to first page on category change if it's not currently the first page
      if ((page !== 1 && query === "cat") || query === "srt" || query === "typ") {
        page = 1;
        queryStringHandler.replace({ pg: "1" });
      }

      handleFetchResults(page, cat, type, srt);
    };

    const handleUpdateSelectValue = (selectedQuery: string, value: string) => {
      selectFilters.forEach((item) => {
        const menuBtnSelectedText = item.querySelector<HTMLSpanElement>(".js-selected-text");
        const groupQuery = item.getAttribute("data-query");
        const filterBtns = item.querySelectorAll<HTMLButtonElement>(".js-filter-btn");

        if (filterBtns && filterBtns.length > 0 && menuBtnSelectedText && groupQuery === selectedQuery.trim()) {
          let selectedIndex = 0;

          filterBtns.forEach((btn, index) => {
            if (btn.getAttribute("data-value") === value) {
              menuBtnSelectedText.innerText = btn.textContent || "";
              selectedIndex = index;
            }
          });

          filterBtns[selectedIndex].disabled = true;
        }
      });

      mobileFilterItems.forEach((item) => {
        const menuBtnSelectedText = item.querySelector<HTMLSpanElement>(".js-selected-mobile-text");
        const groupQuery = item.getAttribute("data-group-query");
        const filterBtns = item.querySelectorAll<HTMLButtonElement>(".js-mobile-filter-btn");

        if (menuBtnSelectedText && filterBtns && filterBtns.length > 0 && groupQuery === selectedQuery.trim()) {
          let selectedIndex = 0;

          filterBtns.forEach((btn, index) => {
            if (btn.getAttribute("data-value") === value) {
              menuBtnSelectedText.innerText = btn.textContent || "";
              selectedIndex = index;
            }
          });

          filterBtns[selectedIndex].disabled = true;
        }
      });
    };

    const handleDisabledPagination = () => {
      if (prevPageBtn) {
        if (page === 1) {
          prevPageBtn.disabled = true;
        } else {
          prevPageBtn.disabled = false;
        }
      }

      if (nextPageBtn) {
        if (page === totalPages) {
          nextPageBtn.disabled = true;
        } else {
          nextPageBtn.disabled = false;
        }
      }
    };

    const handleScrollToTop = () => {
      scrollToElement(resourceIndex);
    };

    const handlePreviousPage = async () => {
      page--;
      handleUpdateQuery("pg", page.toString());
      await handleFetchResults(page, cat, type, srt);
      handleScrollToTop();
    };

    const handleNextPage = async () => {
      page++;
      handleUpdateQuery("pg", page.toString());
      await handleFetchResults(page, cat, type, srt);
      handleScrollToTop();
    };

    if (selectFilters) {
      selectFilters.forEach((item) => {
        const menuBtn = item.querySelector<HTMLSelectElement>(".js-select-menu-btn");
        const menuBtnSelectedText = menuBtn?.querySelector<HTMLSpanElement>(".js-selected-text");
        const filterBtns = item.querySelectorAll<HTMLButtonElement>(".js-filter-btn");
        const groupQuery = item.getAttribute("data-query");

        const handleTabIndex = () => {
          filterBtns.forEach((btn) => {
            if (item.getAttribute("data-open") === "true") {
              btn.tabIndex = 0;
            } else {
              btn.tabIndex = -1;
            }
          });
        };

        const handleToggleMenu = () => {
          if (item.getAttribute("data-open") === "false") {
            item.setAttribute("data-open", "true");
            handleTabIndex();
          } else {
            item.setAttribute("data-open", "false");
            handleTabIndex();
          }
        };

        const handleUpdateSelectedItem = (value: string) => {
          if (menuBtnSelectedText) {
            menuBtnSelectedText.innerText = value;
          }
        };

        filterBtns.forEach((btn, i) => {
          const value = btn.getAttribute("data-value");
          const text = btn.textContent;

          if (groupQuery && text) {
            btn.addEventListener("click", () => {
              handleUpdateSelectedItem(text);
              handleToggleMenu();
              handleUpdateQuery(groupQuery, value || "");
            });

            btn.addEventListener("keydown", (e: KeyboardEvent) => {
              if (e.code === "Escape") {
                item.setAttribute("data-open", "false");
                menuBtn?.focus();
                handleTabIndex();
              }
            });
          }

          if (i === filterBtns.length - 1) {
            btn.addEventListener("keydown", (e: KeyboardEvent) => {
              if (e.code === "Tab" && !e.shiftKey) {
                item.setAttribute("data-open", "false");
                handleTabIndex();
              }
            });
          }
        });

        if (menuBtn) {
          menuBtn.addEventListener("click", handleToggleMenu);
        }

        document.addEventListener("click", (e: MouseEvent) => {
          const target = e.target as Node;
          if (!item.contains(target) && item.getAttribute("data-open") === "true") {
            item.setAttribute("data-open", "false");
            handleTabIndex();
          }
        });
      });
    }
    // MOBILE MENU METHODS
    const handleSetSavedData = () => {
      savedSrt = srt;
      savedCat = cat;
      savedType = type;
      savedPage = page;
    };

    const handleOpenMenu = () => {
      filtersMenu.setAttribute("data-open", "true");
      document.body.style.overflow = "hidden";
      fixedMenuWrapper.style.height = `${window.innerHeight}px`;
      handleSetSavedData();
    };

    const handleCloseMenu = () => {
      filtersMenu.setAttribute("data-open", "false");
      fixedMenuWrapper.style.height = "";
      document.body.style.overflow = "";
    };

    const handleCancelChanges = () => {
      handleCloseMenu();
      if (savedCat !== cat || savedSrt !== srt || savedType !== type || savedPage !== page) {
        queryStringHandler.replace({ srt: savedSrt });
        queryStringHandler.replace({ cat: savedCat });
        queryStringHandler.replace({ typ: savedType });
        queryStringHandler.replace({ pg: savedPage });
        page = savedPage;

        handleFetchResults(savedPage, savedCat, savedType, savedSrt);
      }
    };

    const handleUpdateClearBtn = () => {
      const srtQuery: string = queryStringHandler.getValue("srt");
      const catQuery: string = queryStringHandler.getValue("cat");
      const typeQuery: string = queryStringHandler.getValue("typ");
      let showClearBtn: string = "false";

      if (srtQuery && srtQuery !== "1") {
        showClearBtn = "true";
      }

      if (catQuery) {
        showClearBtn = "true";
      }

      if (typeQuery) {
        showClearBtn = "true";
      }

      clearBtns.forEach((btn) => {
        btn.setAttribute("data-show", showClearBtn);
      });
    };

    const handleApplyChanges = () => {
      handleCloseMenu();
    };

    const handleResize = () => {
      if (filtersMenu.getAttribute("data-open") === "true") {
        handleCloseMenu();
      }
    };

    mobileFilterItems.forEach((item) => {
      const selectedText = item.querySelector<HTMLSpanElement>(".js-selected-mobile-text");
      const filterBtns = item.querySelectorAll<HTMLButtonElement>(".js-mobile-filter-btn");
      const accordionBtn = item.querySelector<HTMLButtonElement>(".js-accordion-btn");
      const accordionContentWrapper = item.querySelector<HTMLDivElement>(".js-accordion-content-wrapper");

      const handleCloseAccordion = () => {
        if (accordionBtn && accordionContentWrapper) {
          accordionBtn.setAttribute("aria-expanded", "false");
          accordionContentWrapper.style.maxHeight = "";
        }
      };

      filterBtns.forEach((btn) => {
        const groupQuery = btn.getAttribute("data-query");
        const queryValue = btn.getAttribute("data-value");

        btn.addEventListener("click", () => {
          if (selectedText) {
            selectedText.innerText = btn.textContent || "";
          }
          if (groupQuery) {
            handleUpdateQuery(groupQuery, queryValue || "");
            handleCloseAccordion();
          }
        });
      });
    });

    if (prevPageBtn) {
      prevPageBtn.addEventListener("click", handlePreviousPage);
    }

    if (nextPageBtn) {
      nextPageBtn.addEventListener("click", handleNextPage);
    }

    if (clearBtns && clearBtns.length > 0) {
      clearBtns.forEach((btn) => {
        btn.addEventListener("click", handleClearFilters);
      });
    }
    // mobile menu actions
    if (openBtn) {
      openBtn.addEventListener("click", handleOpenMenu);
    }

    if (cancelBtn) {
      cancelBtn.addEventListener("click", handleCancelChanges);
    }

    if (applyBtn) {
      applyBtn.addEventListener("click", handleApplyChanges);
    }

    document.addEventListener("resize", handleResize);
    // set values if they're in the query string on load
    document.addEventListener("DOMContentLoaded", () => {
      const pageQuery: number = queryStringHandler.getValue("pg") ? parseInt(queryStringHandler.getValue("pg")) : page;
      const catQuery: string = queryStringHandler.getValue("cat") || cat;
      const typeQuery: string = queryStringHandler.getValue("typ") || type;
      const srtQuery: number = queryStringHandler.getValue("srt") ? parseInt(queryStringHandler.getValue("srt")) : srt;

      // on fetch for results if there is a query param that has filters
      if (pageQuery !== page || catQuery !== cat || typeQuery !== type || srtQuery !== srt) {
        if (pageQuery !== page) {
          page = pageQuery;
        }

        if (catQuery !== cat) {
          cat = catQuery;
        }

        if (typeQuery !== type) {
          type = typeQuery;
        }

        if (srtQuery !== srt) {
          srt = srtQuery;
        }

        handleFetchResults(page, cat, type, srt);
      } else {
        handleDisabledPagination();
        resourceIndex.setAttribute("data-loading", "false");
      }
    });
  }
}
